/* ============================================================================
 *  tvc-security.js — storefront hardening layer (Vedic Crystals)
 *  Loaded once, early, from sections/tvc-security.liquid (header group).
 *
 *  What it does (all defensive, no visual change):
 *   1. Sanitises JSON coming back from the kundli / remedy worker and the
 *      horoscope APIs before the theme scripts insert it with innerHTML:
 *        - svg_string: parsed as SVG, scripts / event handlers / foreignObject
 *          / javascript: links removed
 *        - *url / href / contact fields: only http(s), mailto, tel and
 *          same-site relative paths allowed; quotes and angle brackets encoded
 *        - every other string: < and > removed (plain astrology text never
 *          contains them, so nothing visible changes)
 *   2. Stops the browser-side "NEW PAYPAL ORDER" call to api.whatsapp.com,
 *      which put customer name/email/phone/address in a third-party URL and
 *      had no effect (the order data already goes to the worker, the Sheets
 *      log and the /contact backup).
 *   3. Ignores payment-error postMessages from origins that are not the
 *      store, PayPal, Razorpay or Shopify, so other frames can't spoof the
 *      payment-failed popup.
 *   4. Tells Microsoft Clarity to mask the custom PayPal checkout modal.
 *   5. Rewrites outdated collection links that tvc-app.js builds in JS.
 *
 *  To roll back: delete the "tvc-security" entry from sections/header-group.json.
 * ==========================================================================*/
(function () {
  'use strict';
  if (window.__tvcSecurityLoaded) return;
  window.__tvcSecurityLoaded = true;

  /* ---------- config ---------- */
  var SANITISE_HOSTS = [
    'late-grass-18d9.gauravbsharma.workers.dev',
    'horoscope-app-api.vercel.app',
    'freehoroscopeapi.com',
    'www.freehoroscopeapi.com'
  ];
  var BLOCK_REQUEST = [
    /^https?:\/\/api\.whatsapp\.com\/send/i
  ];
  var TRUSTED_MESSAGE_ORIGINS = [
    /^https:\/\/([a-z0-9-]+\.)*thevediccrystals\.com$/i,
    /^https:\/\/([a-z0-9-]+\.)*paypal\.com$/i,
    /^https:\/\/([a-z0-9-]+\.)*paypalobjects\.com$/i,
    /^https:\/\/([a-z0-9-]+\.)*razorpay\.com$/i,
    /^https:\/\/([a-z0-9-]+\.)*shopify\.com$/i,
    /^https:\/\/([a-z0-9-]+\.)*myshopify\.com$/i
  ];
  var URL_KEY = /(^|_)(url|href|link|contact)$|Url$|Href$|Link$/;

  /* ---------- helpers ---------- */
  function hostOf(u) {
    try { return new URL(u, location.href).hostname.toLowerCase(); } catch (e) { return ''; }
  }

  function safeUrl(u) {
    var s = String(u == null ? '' : u).trim();
    if (!s) return s;
    var ok = /^(https?:|mailto:|tel:|#)/i.test(s) || (/^\//.test(s) && !/^\/\//.test(s));
    if (!ok) return '#';
    return s.replace(/"/g, '%22').replace(/'/g, '%27').replace(/</g, '%3C').replace(/>/g, '%3E').replace(/`/g, '%60');
  }

  function stripTags(s) { return String(s).replace(/[<>]/g, ''); }

  function sanitiseSvg(svg) {
    if (typeof svg !== 'string' || !svg) return svg;
    try {
      var doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
      var root = doc.documentElement;
      if (!root || root.nodeName.toLowerCase() !== 'svg' || doc.getElementsByTagName('parsererror').length) return '';
      var bad = root.querySelectorAll('script, foreignObject, iframe, object, embed, handler, listener');
      for (var i = 0; i < bad.length; i++) bad[i].parentNode.removeChild(bad[i]);
      var all = [root].concat(Array.prototype.slice.call(root.querySelectorAll('*')));
      all.forEach(function (el) {
        Array.prototype.slice.call(el.attributes).forEach(function (a) {
          var n = a.name.toLowerCase();
          var v = String(a.value || '').replace(/[\s\u0000-\u001f]/g, '').toLowerCase();
          if (n.indexOf('on') === 0) { el.removeAttribute(a.name); return; }
          if ((n === 'href' || n === 'xlink:href' || n === 'src' || n === 'action' || n === 'formaction') &&
              (v.indexOf('javascript:') === 0 || v.indexOf('data:') === 0 || v.indexOf('vbscript:') === 0)) {
            el.removeAttribute(a.name);
          }
        });
      });
      return new XMLSerializer().serializeToString(root);
    } catch (e) {
      return '';
    }
  }

  function sanitise(value, key) {
    if (typeof value === 'string') {
      if (key === 'svg_string' || key === 'svg') return sanitiseSvg(value);
      if (key && URL_KEY.test(key)) return safeUrl(value);
      return stripTags(value);
    }
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i++) value[i] = sanitise(value[i], key);
      return value;
    }
    if (value && typeof value === 'object') {
      Object.keys(value).forEach(function (k) { value[k] = sanitise(value[k], k); });
      return value;
    }
    return value;
  }

  /* ---------- 1 + 2: fetch wrapper ---------- */
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = (input && typeof input === 'object' && 'url' in input) ? input.url : String(input);

      for (var i = 0; i < BLOCK_REQUEST.length; i++) {
        if (BLOCK_REQUEST[i].test(url)) {
          return Promise.resolve(new Response(null, { status: 204, statusText: 'Blocked by tvc-security' }));
        }
      }

      var p = nativeFetch.apply(this, arguments);
      if (SANITISE_HOSTS.indexOf(hostOf(url)) === -1) return p;

      return p.then(function (res) {
        var type = (res.headers.get('content-type') || '').toLowerCase();
        if (res.type === 'opaque' || type.indexOf('json') === -1) return res;
        return res.clone().text().then(function (txt) {
          var data;
          try { data = JSON.parse(txt); } catch (e) { return res; }
          var clean = JSON.stringify(sanitise(data, ''));
          var headers = new Headers(res.headers);
          headers.delete('content-length');
          return new Response(clean, { status: res.status, statusText: res.statusText, headers: headers });
        }).catch(function () { return res; });
      });
    };
  }

  /* ---------- 3: postMessage origin filter ---------- */
  function trustedOrigin(o) {
    if (!o || o === 'null') return false;
    if (o === location.origin) return true;
    for (var i = 0; i < TRUSTED_MESSAGE_ORIGINS.length; i++) if (TRUSTED_MESSAGE_ORIGINS[i].test(o)) return true;
    return false;
  }
  window.addEventListener('message', function (e) {
    var d = e.data;
    if (typeof d === 'string') { try { d = JSON.parse(d); } catch (err) { return; } }
    if (!d || typeof d !== 'object') return;
    var looksLikePaymentError = ('payment_failed' in d) || ('error' in d && !('jsonrpc' in d));
    if (looksLikePaymentError && !trustedOrigin(e.origin)) {
      e.stopImmediatePropagation();
    }
  }, true);

  /* ---------- 4: Clarity masking for the PayPal checkout modal ---------- */
  var MASK_SELECTOR = '.tvc-pp-checkout-overlay, .tvc-pp-form, #tvc-paypal-smart-buttons';
  function mask(root) {
    try {
      var els = (root || document).querySelectorAll(MASK_SELECTOR);
      for (var i = 0; i < els.length; i++) {
        if (!els[i].hasAttribute('data-clarity-mask')) els[i].setAttribute('data-clarity-mask', 'True');
      }
    } catch (e) {}
  }
  function startMasking() {
    mask(document);
    if ('MutationObserver' in window && document.body) {
      new MutationObserver(function () { mask(document); })
        .observe(document.body, { childList: true, subtree: true });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startMasking);
  else startMasking();

  /* ---------- 5: outdated links built by tvc-app.js ---------- */
  // tvc-app.js (minified) builds these links in JS. They point at old collection
  // handles; URL redirects catch them, but this saves visitors the extra hop.
  var LINK_FIX = {
    '/collections/buy-emerald-panna-stone': '/collections/emerald-panna-stone',
    '/collections/buy-pearl-moti-stone': '/collections/moti-stone',
    '/collections/buy-red-coral-moonga': '/collections/buy-red-coral-stone-moonga',
    '/collections/buy-yellow-sapphire-pukhraj-stone': '/collections/yellow-sapphire-pukhraj',
    '/collections/cats-eye-lehsuniya': '/collections/lehsunia-stone',
    '/collections/citrine-stone-1': '/collections/natural-citrine-stone-1',
    '/collections/gemstone-pendant': '/collections/gemstone-pendants',
    '/collections/hessonite-gomed': '/collections/gomed-nag',
    '/collections/opal-gemstone': '/collections/white-opal-stone',
    '/collections/peridot-stone': '/collections/peridot-stone-price'
  };
  function fixLinks(root) {
    try {
      var as = (root && root.querySelectorAll) ? root.querySelectorAll('a[href^="/collections/"]') : [];
      for (var i = 0; i < as.length; i++) {
        var h = as[i].getAttribute('href');
        var path = h.split(/[?#]/)[0].replace(/\/$/, '');
        if (LINK_FIX.hasOwnProperty(path)) as[i].setAttribute('href', LINK_FIX[path] + h.slice(path.length));
      }
    } catch (e) {}
  }
  function startLinkFix() {
    fixLinks(document);
    if ('MutationObserver' in window && document.body) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var m = muts[i];
          if (m.type === 'attributes') { fixLinks(m.target.parentNode || document); continue; }
          for (var j = 0; j < m.addedNodes.length; j++) {
            var n = m.addedNodes[j];
            if (n.nodeType === 1) { if (n.tagName === 'A') fixLinks(n.parentNode || document); else fixLinks(n); }
          }
        }
      }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startLinkFix);
  else startLinkFix();
})();
